import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSwipeable } from 'react-swipeable';
import {
  Box,
  Typography,
  Drawer,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Button,
  LinearProgress,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Collapse,
  Breadcrumbs,
  Link,
  Paper,
  Tooltip,
  Fab,
  Zoom,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  PlayCircle as PlayIcon,
  CheckCircle as CompletedIcon,
  RadioButtonUnchecked as NotStartedIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  ExpandLess,
  ExpandMore,
  Home as HomeIcon,
  School as CourseIcon,
  ListAlt as LessonListIcon,
  SwipeLeft as SwipeIcon,
} from '@mui/icons-material';
import { courseApi, LessonDetails, LessonCompletionResult } from '../services/api';
import ContentRenderer from '../components/ContentRenderer';

const SIDEBAR_WIDTH = 320;
const MAIN_DRAWER_WIDTH = 240; // From MainLayout

interface Lesson {
  id: string;
  slug: string;
  title: string;
  contentType: string;
  estimatedDurationMinutes: number | null;
  pointsReward: number;
  sortOrder: number;
}

interface Module {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  sortOrder: number;
  lessons: Lesson[];
}

interface Course {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  pointsReward: number;
  modules: Module[];
  enrollment: { status: string; progressPercent: number } | null;
}

interface LessonProgress {
  lessonId: string;
  status: 'not-started' | 'in-progress' | 'completed';
}

export default function LessonPage() {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [completedLessons, setCompletedLessons] = useState<Set<string>>(new Set());
  const [startTime] = useState<number>(Date.now());
  const [showSwipeHint, setShowSwipeHint] = useState(false);

  // Use lessonId directly from URL params
  const activeLessonId = lessonId;

  // Fetch course with modules (using ID)
  const { data: course, isLoading: courseLoading } = useQuery({
    queryKey: ['course', courseId],
    queryFn: () => courseApi.getCourse(courseId!).then((r) => r.data as Course),
    enabled: !!courseId,
  });

  // Fetch current lesson details (using activeLessonId to trigger refetch on state change)
  const { data: lessonDetails, isLoading: lessonLoading, error: lessonError, isFetching } = useQuery({
    queryKey: ['lesson', courseId, activeLessonId],
    queryFn: () => courseApi.getLesson(courseId!, activeLessonId!).then((r) => r.data),
    enabled: !!courseId && !!activeLessonId,
    staleTime: 0,
    gcTime: 0,
  });

  // Check if we have the correct data for the current URL
  // This prevents showing stale content during navigation
  const isContentStale = lessonDetails && lessonDetails.id !== activeLessonId && lessonDetails.slug !== activeLessonId;
  const showLoading = lessonLoading || isContentStale || (lessonId !== activeLessonId);

  // Complete lesson mutation - use stable IDs from loaded data
  const completeMutation = useMutation({
    mutationFn: (data: { score?: number; timeSpentSeconds?: number }) =>
      courseApi.completeLesson(course?.id || courseId!, lessonDetails?.id || lessonId!, data),
    onSuccess: (response) => {
      setCompletedLessons((prev) => new Set(prev).add(lessonDetails?.id || ''));
      queryClient.invalidateQueries({ queryKey: ['course', courseId] });
      queryClient.invalidateQueries({ queryKey: ['progress-summary'] });
    },
  });

  // Get all lessons in order
  const allLessons = course?.modules
    ?.sort((a, b) => a.sortOrder - b.sortOrder)
    .flatMap((module) =>
      (module.lessons || []).sort((a, b) => a.sortOrder - b.sortOrder).map((lesson) => ({
        ...lesson,
        moduleId: module.id,
        moduleTitle: module.title,
      }))
    ) || [];

  // Helper to check if string is UUID format
  const isUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

  // Find current lesson index by ID or slug (supports both UUID and slug-based URLs)
  // Use activeLessonId which is synced with URL to ensure correct index calculation
  const currentIndex = allLessons.findIndex((l) => l.id === activeLessonId || l.slug === activeLessonId);
  const currentLesson = currentIndex >= 0 ? allLessons[currentIndex] : null;
  const prevLesson = currentIndex > 0 ? allLessons[currentIndex - 1] : null;
  const nextLesson = currentIndex < allLessons.length - 1 ? allLessons[currentIndex + 1] : null;

  // Redirect to UUID-based URL if using slugs (to ensure consistent URLs)
  // Using a ref to prevent multiple redirects
  const redirectedRef = React.useRef(false);

  useEffect(() => {
    // Reset redirect flag when lessonId changes
    redirectedRef.current = false;
  }, [lessonId]);

  useEffect(() => {
    // Skip if already redirected or missing data
    if (redirectedRef.current || !course || !currentLesson || !courseId || !lessonId) {
      return;
    }

    const courseIsSlug = !isUUID(courseId);
    const lessonIsSlug = !isUUID(lessonId);

    if (courseIsSlug || lessonIsSlug) {
      redirectedRef.current = true;
      const newUrl = `/courses/${course.id}/lessons/${currentLesson.id}`;
      navigate(newUrl, { replace: true });
    }
  }, [course, currentLesson, courseId, lessonId, navigate]);

  // Expand module containing current lesson
  useEffect(() => {
    if (currentLesson) {
      setExpandedModules((prev) => new Set(prev).add(currentLesson.moduleId));
    }
  }, [currentLesson]);

  // Handle content completion
  const handleContentComplete = useCallback(
    (data: any) => {
      const timeSpentSeconds = Math.floor((Date.now() - startTime) / 1000);
      completeMutation.mutate({
        score: data?.score,
        timeSpentSeconds,
      });
    },
    [startTime, completeMutation]
  );

  // Navigate to lesson by ID - use window.location.href for reliable navigation
  // This causes a full page load but ensures content updates correctly
  const navigateToLesson = useCallback((lesson: { id: string }) => {
    const targetCourseId = course?.id || courseId;
    const targetUrl = `/courses/${targetCourseId}/lessons/${lesson.id}`;

    // Show a loading state before navigation
    const loadingOverlay = document.createElement('div');
    loadingOverlay.id = 'lesson-nav-overlay';
    loadingOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.8);
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: opacity 0.15s ease;
    `;
    loadingOverlay.innerHTML = '<div style="width: 40px; height: 40px; border: 4px solid #1976d2; border-top-color: transparent; border-radius: 50%; animation: spin 1s linear infinite;"></div>';

    // Add spinner animation
    const style = document.createElement('style');
    style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(style);
    document.body.appendChild(loadingOverlay);

    // Navigate after overlay is visible
    setTimeout(() => {
      window.location.href = targetUrl;
    }, 50);
  }, [course?.id, courseId]);

  // Swipe handlers for mobile lesson navigation
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (nextLesson && isMobile) {
        navigateToLesson(nextLesson);
      }
    },
    onSwipedRight: () => {
      if (prevLesson && isMobile) {
        navigateToLesson(prevLesson);
      }
    },
    preventScrollOnSwipe: false,
    trackMouse: false,
    delta: 50, // Minimum swipe distance
  });

  // Show swipe hint on first mobile visit
  useEffect(() => {
    if (isMobile) {
      const hasSeenHint = localStorage.getItem('lesson-swipe-hint-seen');
      if (!hasSeenHint) {
        setShowSwipeHint(true);
        const timer = setTimeout(() => {
          setShowSwipeHint(false);
          localStorage.setItem('lesson-swipe-hint-seen', 'true');
        }, 4000);
        return () => clearTimeout(timer);
      }
    }
  }, [isMobile]);

  // Toggle module expansion
  const toggleModule = (moduleId: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  };

  // Calculate progress
  const totalLessons = allLessons.length;
  const completedCount = completedLessons.size;
  const progressPercent = totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0;

  // Loading state - also show loading when content is stale (URL changed but data not yet updated)
  if (courseLoading || showLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (lessonError || !course || !lessonDetails) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('errors.notFound')} - Lektion nicht gefunden
        </Alert>
        <Button startIcon={<HomeIcon />} onClick={() => navigate(`/courses/${course?.id || courseId}`)}>
          Zurueck zum Kurs
        </Button>
      </Box>
    );
  }

  // Sidebar content
  const sidebarContent = (
    <Box sx={{ width: SIDEBAR_WIDTH, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2" color="text.secondary">
            Kursinhalt
          </Typography>
          {isMobile && (
            <IconButton size="small" onClick={() => setSidebarOpen(false)}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
        <Typography variant="h6" noWrap title={course.title}>
          {course.title}
        </Typography>
        <Box display="flex" alignItems="center" gap={1} mt={1}>
          <LinearProgress
            variant="determinate"
            value={course.enrollment?.progressPercent || progressPercent}
            sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
          />
          <Typography variant="caption" color="text.secondary">
            {course.enrollment?.progressPercent || progressPercent}%
          </Typography>
        </Box>
      </Box>

      {/* Module list */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List disablePadding>
          {course.modules
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((module) => (
              <React.Fragment key={module.id}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => toggleModule(module.id)} sx={{ py: 1.5 }}>
                    <ListItemText
                      primary={module.title}
                      secondary={`${module.lessons.length} Lektionen`}
                      primaryTypographyProps={{ fontWeight: 'medium' }}
                    />
                    {expandedModules.has(module.id) ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={expandedModules.has(module.id)} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {module.lessons
                      .sort((a, b) => a.sortOrder - b.sortOrder)
                      .map((lesson) => {
                        const isActive = lesson.id === activeLessonId || lesson.slug === activeLessonId || lesson.id === lessonDetails?.id;
                        const isCompleted = completedLessons.has(lesson.id);

                        return (
                          <ListItem key={lesson.id} disablePadding>
                            <ListItemButton
                              onClick={() => navigateToLesson(lesson)}
                              selected={isActive}
                              sx={{ pl: 4 }}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                {isCompleted ? (
                                  <CompletedIcon color="success" fontSize="small" />
                                ) : isActive ? (
                                  <PlayIcon color="primary" fontSize="small" />
                                ) : (
                                  <NotStartedIcon color="disabled" fontSize="small" />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={lesson.title}
                                secondary={
                                  lesson.estimatedDurationMinutes
                                    ? `${lesson.estimatedDurationMinutes} Min.`
                                    : undefined
                                }
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  fontWeight: isActive ? 'bold' : 'normal',
                                }}
                              />
                              {lesson.pointsReward > 0 && (
                                <Chip size="small" label={`${lesson.pointsReward}P`} variant="outlined" />
                              )}
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                  </List>
                </Collapse>
                <Divider />
              </React.Fragment>
            ))}
        </List>
      </Box>

      {/* Footer actions */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<CourseIcon />}
          onClick={() => navigate(`/courses/${course.id}`)}
        >
          Kursuebersicht
        </Button>
      </Box>
    </Box>
  );

  return (
    // Use negative margins to fill the MainLayout content area while staying in normal document flow
    // This ensures React properly unmounts the component when navigating away
    <Box
      {...(isMobile ? swipeHandlers : {})}
      sx={{
        display: 'flex',
        height: 'calc(100vh - 64px)', // Fill viewport below AppBar
        overflow: 'hidden',
        bgcolor: 'background.default',
        // Counter MainLayout padding to extend to edges
        m: { xs: -2, sm: -3 },
        width: { xs: 'calc(100% + 32px)', sm: 'calc(100% + 48px)' },
        touchAction: isMobile ? 'pan-y' : 'auto', // Allow vertical scrolling while enabling horizontal swipe
      }}>
      {/* Sidebar - Desktop */}
      {!isMobile && (
        <Box
          sx={{
            width: sidebarOpen ? SIDEBAR_WIDTH : 0,
            flexShrink: 0,
            transition: 'width 0.3s',
            overflow: 'hidden',
            borderRight: 1,
            borderColor: 'divider',
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* Sidebar - Mobile Bottom Sheet */}
      {isMobile && (
        <SwipeableDrawer
          anchor="bottom"
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onOpen={() => setSidebarOpen(true)}
          disableSwipeToOpen
          PaperProps={{
            sx: {
              width: '100%',
              maxHeight: '80vh',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
            },
          }}
        >
          {/* Drag handle */}
          <Box
            sx={{
              width: 40,
              height: 4,
              bgcolor: 'grey.400',
              borderRadius: 2,
              mx: 'auto',
              my: 1.5,
            }}
          />
          {sidebarContent}
        </SwipeableDrawer>
      )}

      {/* FAB for lesson list on mobile */}
      {isMobile && (
        <Zoom in={!sidebarOpen}>
          <Fab
            color="primary"
            size="medium"
            onClick={() => setSidebarOpen(true)}
            sx={{
              position: 'fixed',
              bottom: 80, // Above bottom navigation
              right: 16,
              zIndex: 1000,
            }}
            aria-label="Lektionsliste oeffnen"
          >
            <LessonListIcon />
          </Fab>
        </Zoom>
      )}

      {/* Main content area - flex container for navigation + content */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Navigation bar - sticky within the lesson content area */}
        <Paper
          elevation={3}
          sx={{
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10, // Within lesson content only, not affecting MainLayout
            px: 2,
            py: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            borderRadius: 0,
            bgcolor: 'background.paper',
            borderBottom: '2px solid',
            borderColor: 'primary.main',
          }}
        >
          {/* First row: Menu, Title, Counter */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              {(isMobile || !sidebarOpen) && (
                <IconButton size="small" onClick={() => setSidebarOpen(true)}>
                  <MenuIcon />
                </IconButton>
              )}
              {!isMobile && sidebarOpen && (
                <IconButton size="small" onClick={() => setSidebarOpen(false)}>
                  <CloseIcon />
                </IconButton>
              )}
              <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{ maxWidth: 400 }}>
                {lessonDetails.title}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
              Lektion {currentIndex + 1} / {totalLessons}
            </Typography>
          </Box>

          {/* Second row: Navigation buttons - touch-optimized for mobile */}
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
            <Tooltip title={isMobile ? 'Wische nach rechts' : ''} open={showSwipeHint && !!prevLesson} arrow placement="top">
              <span>
                <Button
                  size={isMobile ? 'medium' : 'small'}
                  startIcon={<PrevIcon />}
                  onClick={() => prevLesson && navigateToLesson(prevLesson)}
                  disabled={!prevLesson}
                  variant="outlined"
                  sx={{
                    minHeight: { xs: 44, sm: 'auto' }, // Touch-friendly on mobile
                    minWidth: { xs: 44, sm: 'auto' },
                    px: { xs: 1.5, sm: 2 },
                  }}
                >
                  {isMobile ? '' : 'Zurueck'}
                </Button>
              </span>
            </Tooltip>

            <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1, justifyContent: 'center' }}>
              {!completedLessons.has(lessonDetails.id) && (
                <Button
                  size={isMobile ? 'medium' : 'small'}
                  variant="contained"
                  color="success"
                  startIcon={completeMutation.isPending ? <CircularProgress size={14} /> : <CompletedIcon />}
                  onClick={() => handleContentComplete({})}
                  disabled={completeMutation.isPending}
                  sx={{
                    minHeight: { xs: 44, sm: 'auto' },
                    px: { xs: 2, sm: 2 },
                  }}
                >
                  {isMobile ? 'Fertig' : 'Abgeschlossen'}
                </Button>
              )}
              {completedLessons.has(lessonDetails.id) && (
                <Chip
                  size={isMobile ? 'medium' : 'small'}
                  icon={<CompletedIcon />}
                  label="Erledigt"
                  color="success"
                  sx={{ height: { xs: 36, sm: 'auto' } }}
                />
              )}
            </Box>

            <Tooltip title={isMobile ? 'Wische nach links' : ''} open={showSwipeHint && !!nextLesson} arrow placement="top">
              <Button
                size={isMobile ? 'medium' : 'small'}
                endIcon={<NextIcon />}
                variant="contained"
                color="primary"
                onClick={() => {
                  if (nextLesson) {
                    navigateToLesson(nextLesson);
                  } else {
                    navigate(`/courses/${course.id}`);
                  }
                }}
                sx={{
                  minHeight: { xs: 44, sm: 'auto' },
                  minWidth: { xs: 44, sm: 'auto' },
                  px: { xs: 1.5, sm: 2 },
                }}
              >
                {isMobile ? '' : nextLesson ? 'Weiter' : 'Zum Kurs'}
              </Button>
            </Tooltip>
          </Box>

          {/* Swipe hint for mobile */}
          {isMobile && showSwipeHint && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                py: 0.5,
                color: 'text.secondary',
                fontSize: '0.75rem',
              }}
            >
              <SwipeIcon fontSize="small" />
              <Typography variant="caption">Wische zum Navigieren</Typography>
            </Box>
          )}
        </Paper>

        {/* Content area - fills remaining height */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            height: '100%',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            opacity: isFetching ? 0.6 : 1,
            transition: 'opacity 0.2s ease',
          }}
        >
          {/* Loading indicator for lesson transitions */}
          {isFetching && (
            <LinearProgress
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 5,
                height: 3,
              }}
            />
          )}
          <ContentRenderer
            key={lessonDetails.id}
            contentType={lessonDetails.contentType}
            contentUrl={lessonDetails.contentUrl}
            contentText={lessonDetails.contentText}
            lessonId={lessonDetails.id}
            lessonSlug={lessonDetails.id}
            courseSlug={course.id}
            onComplete={handleContentComplete}
            onProgress={() => {}}
            onNavigateNext={() => {
              if (nextLesson) {
                navigateToLesson(nextLesson);
              }
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
